export const QUIZ_PASS_MARK = 50;

/** Unwrap nested API shapes: returnData.item, returnData.result, flat body, etc. */
export function unwrapQuizResultPayload(data) {
  if (data == null) return null;
  if (Array.isArray(data)) {
    return data.length ? unwrapQuizResultPayload(data[0]) : null;
  }
  if (typeof data !== 'object') return null;

  const hasScoreFields = (obj) =>
    obj != null &&
    typeof obj === 'object' &&
    (obj.count_correct_answer != null ||
      obj.countCorrect != null ||
      obj.passPercentage != null ||
      obj.pass_percentage != null ||
      obj.passed != null ||
      obj.total_questions != null);

  if (hasScoreFields(data)) return data;

  const candidates = [
    data.returnData,
    data.item,
    data.result,
    data.results,
    data.data,
    data.score_summary,
    data.summary
  ];

  for (const candidate of candidates) {
    const unwrapped = unwrapQuizResultPayload(candidate);
    if (unwrapped && hasScoreFields(unwrapped)) return unwrapped;
  }

  return hasScoreFields(data) ? data : null;
}

/** Parse quiz results from iresults or submit response. */
export function parseQuizResults(data) {
  const returnData = unwrapQuizResultPayload(data);
  if (!returnData) return null;

  const listRaw = returnData.list_of_item ?? returnData.results ?? returnData.answers ?? returnData.list ?? returnData.data;
  const list = Array.isArray(listRaw) ? listRaw : (listRaw?.data ?? []);

  const countCorrect = Number(
    returnData.count_correct_answer ?? returnData.countCorrect ?? returnData.score ?? returnData.total_score ?? returnData.marks
  );
  const totalQuestions = Number(
    returnData.total_questions ?? returnData.totalQuestions ?? returnData.total ?? returnData.total_marks ?? (list.length || 0)
  );
  const passMark = Number(returnData.pass_mark ?? returnData.passMark ?? QUIZ_PASS_MARK);

  let percentage = returnData.passPercentage ?? returnData.pass_percentage ?? returnData.percentage ?? returnData.score_percentage;
  if ((percentage == null || Number.isNaN(Number(percentage))) && !Number.isNaN(countCorrect) && totalQuestions > 0) {
    percentage = (countCorrect / totalQuestions) * 100;
  }

  const pct = Number(percentage);
  const computedPct = !Number.isNaN(pct)
    ? pct
    : (!Number.isNaN(countCorrect) && totalQuestions > 0 ? (countCorrect / totalQuestions) * 100 : null);

  const hasScore =
    returnData.count_correct_answer != null ||
    returnData.passPercentage != null ||
    returnData.passed != null ||
    computedPct != null ||
    (!Number.isNaN(countCorrect) && totalQuestions > 0) ||
    list.length > 0;

  let passed;
  if (typeof returnData.passed === 'boolean') {
    passed = returnData.passed;
  } else if (returnData.passed === 'true' || returnData.passed === 1 || returnData.passed === '1') {
    passed = true;
  } else if (returnData.passed === 'false' || returnData.passed === 0 || returnData.passed === '0') {
    passed = false;
  } else {
    passed = computedPct != null && computedPct >= (Number.isNaN(passMark) ? QUIZ_PASS_MARK : passMark);
  }

  return {
    countCorrect: Number.isNaN(countCorrect) ? null : countCorrect,
    totalQuestions: Number.isNaN(totalQuestions) ? null : totalQuestions,
    percentage: computedPct,
    passMark: Number.isNaN(passMark) ? QUIZ_PASS_MARK : passMark,
    passed,
    hasAttempt: hasScore,
    list
  };
}

export function formatQuizScoreSummary(result) {
  if (!result) return '';
  const parts = [];
  if (result.countCorrect != null && result.totalQuestions != null) {
    parts.push(`Correct: ${result.countCorrect} / ${result.totalQuestions}`);
  }
  if (result.percentage != null && !Number.isNaN(result.percentage)) {
    parts.push(`Score: ${Number(result.percentage).toFixed(1)}%`);
  }
  if (result.passMark != null) {
    parts.push(`Pass mark: ${result.passMark}%`);
  }
  return parts.join(' · ');
}
